import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { getTimezoneShortName, parseLocalDateTime } from './analysisTabDateTime';

export interface AnalysisChartSensorData {
  sensorName: string;
  unit: string;
  dataPoints: { date: string; value: number }[];
}

function isRelaySensorForSeries(name: string, sensorData?: AnalysisChartSensorData): boolean {
  const MEASUREMENT_UNITS = ['°c', '°f', '%', 'ppm', 'mg/l', 'ph', 'mv', 'ma', 'v', 'a', 'w', 'bar', 'psi', 'l/h', 'm³/h', 'gpm'];
  const RELAY_KEYWORDS = ['relay', 'acid', 'pump', 'valve'];

  if (sensorData) {
    let hasOnlyBinaryValues = true;
    for (const point of sensorData.dataPoints) {
      if (point.value !== 0 && point.value !== 1) {
        hasOnlyBinaryValues = false;
        break;
      }
    }
    const hasNoMeasurementUnit = !sensorData.unit || !MEASUREMENT_UNITS.includes(sensorData.unit.toLowerCase());
    const lowerName = name.toLowerCase();
    const hasRelayNaming = RELAY_KEYWORDS.some(k => lowerName.includes(k)) || lowerName === 'unassigned';
    return (hasOnlyBinaryValues && hasNoMeasurementUnit) || hasRelayNaming;
  }

  const lowerName = name.toLowerCase();
  return RELAY_KEYWORDS.some(k => lowerName.includes(k)) || lowerName === 'unassigned';
}

export function createAnalysisChartSeries(
  chart: am5xy.XYChart,
  root: am5.Root,
  name: string,
  color: string,
  xAxis: am5xy.DateAxis<am5xy.AxisRendererX>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>,
  data: { date: string; value: number }[],
  cursor: am5xy.XYCursor,
  sensors: AnalysisChartSensorData[],
  sensorData?: AnalysisChartSensorData
): void {
  const useStepLine = isRelaySensorForSeries(name, sensorData);

  const series = useStepLine
    ? chart.series.push(
        am5xy.StepLineSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: 'value',
          valueXField: 'date',
          stacked: false,
          connect: false,
          noRisers: false,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'left',
            y: am5.percent(50),
            forceHidden: true,
          }),
          snapTooltip: true,
          stroke: am5.color(color),
          fill: am5.color(color),
        })
      )
    : chart.series.push(
        am5xy.LineSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: 'value',
          valueXField: 'date',
          stacked: false,
          connect: false,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'left',
            y: am5.percent(50),
            forceHidden: true,
          }),
          snapTooltip: true,
          stroke: am5.color(color),
          fill: am5.color(color),
        })
      );

  series.strokes.template.set('strokeWidth', useStepLine ? 3 : 2);
  series.fills.template.setAll({ fillOpacity: 0, visible: false });

  series.bullets.push(() => {
    const circle = am5.Circle.new(root, {
      radius: 0,
      fill: am5.color('#000'),
      stroke: am5.color('#fff'),
      strokeWidth: 3,
      interactive: true,
      opacity: 0,
    });
    circle.states.create('default', { opacity: 0 });
    circle.states.create('hover', { radius: 6, opacity: 1 });
    return am5.Bullet.new(root, { locationX: 0.5, sprite: circle });
  });

  let previousBulletSprites: am5.Sprite[] = [];
  const cursorMoved = () => {
    for (const sprite of previousBulletSprites) {
      sprite?.unhover();
    }
    previousBulletSprites = [];
    chart.series.each((chartSeries) => {
      const dataItem = chartSeries?.get('tooltip')?.dataItem;
      if (dataItem) {
        const bulletSprite = dataItem?.bullets?.length && dataItem?.bullets[0]?.get('sprite');
        if (bulletSprite) {
          bulletSprite.hover();
          previousBulletSprites.push(bulletSprite as am5.Sprite);
        }
      }
    });

    const cursorPoint = cursor.getPrivate('point');
    const chartWidth = chart.width();
    const tooltip = chart.plotContainer.get('tooltip');
    const toolTipPosition = tooltip?.get('pointerOrientation');
    if (cursorPoint) {
      const xPos = cursorPoint.x;
      if (xPos < chartWidth / 3) {
        if (toolTipPosition !== 'left') {
          tooltip?.set('pointerOrientation', 'left');
          tooltip?.set('dx', 15);
        }
      } else if (toolTipPosition !== 'right') {
        tooltip?.set('pointerOrientation', 'right');
        tooltip?.set('dx', -15);
      }
    }

    let showDateHeader = true;
    let html = `<div style="width:auto;display:flex;flex-direction:column;font-size: 13px;color: #F7F9F9;font-family: 'Inter';font-weight: 400">`;
    chart.series.each((chartSeries) => {
      const tooltipDataItem = chartSeries.get('tooltipDataItem');
      if (!tooltipDataItem) {
        return;
      }
      if (showDateHeader) {
        const valueX = tooltipDataItem.get('valueX');
        if (valueX != null) {
          const date = new Date(valueX as number);
          const formattedDate = date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
          const formattedTime = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          const timezoneShortName = getTimezoneShortName();
          html += `<div style="border-bottom: 1px solid #4A5463;padding:10px"> ${formattedDate}, ${formattedTime}, ${timezoneShortName}</div>`;
          showDateHeader = false;
        }
      }

      const valueY = tooltipDataItem.get('valueY');
      const seriesName = chartSeries.get('name') || 'Unnamed Series';
      const seriesColor = chartSeries.get('stroke')?.toString() || '#000000';
      if (chartSeries.isVisible() === true) {
        const baseSensorName = seriesName.replace(/\s*\([^)]*\)\s*$/, '');
        const sensor = sensors.find(s => s.sensorName === baseSensorName);
        const unit = sensor?.unit || '';
        const individualValue = valueY == null ? '' : `${(+valueY).toFixed(2)}${unit ? ` ${unit}` : ''}`;
        html += `<div style="display: flex; align-items: center;flex-direction: row;gap: 20px;justify-content: space-between;padding:5px">
            <div style="height:24px;align-items: center;display: flex">
              <span style="border:1px solid ${seriesColor};height:24px;margin-right: 5px;"></span>
              <span>${seriesName}</span>
            </div>
            <div>${individualValue}</div>
          </div>`;
      }
    });
    html += '</div>';
    chart.plotContainer.set('tooltipHTML', html);
  };

  cursor.events.on('cursormoved', cursorMoved);

  const convertedArray = data
    .map((item) => {
      const timestamp = parseLocalDateTime(item.date);
      if (isNaN(timestamp)) {
        return null;
      }
      return { ...item, date: timestamp };
    })
    .filter((item): item is { date: number; value: number } => item !== null);

  const sortedData = convertedArray.sort((a, b) => a.date - b.date);
  const cleandata = sortedData.filter(item =>
    item.value !== null &&
    item.value !== undefined &&
    !isNaN(item.value) &&
    isFinite(item.value)
  );

  xAxis.set('baseInterval', { timeUnit: 'minute', count: 5 });
  series.data.setAll(cleandata);
}
